/**
 * Firm Settings › Services and workflows › CFO benchmarks: per industry, the default KPI targets
 * (copied to a client when staff choose to) and the published figures shown beside the firm's own
 * peers on the Benchmarks tab, each with its source and year.
 */
import { BENCH_KPIS, cfoDefaultsOf, Exact, INDUSTRIES, KPI_DEFINITIONS, type CfoDefaults, type Industry, type KpiKey } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Loading, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const trim = (s: string) => (s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s);
const isPercent = (k: KpiKey) => KPI_DEFINITIONS[k].kind === "percent";
const DECIMAL = /^-?\d{1,9}(\.\d{1,6})?$/;

/** Stored values are in the KPI's own units (a fraction for percentages); staff type percentages as percent. */
function shown(k: KpiKey, v: string | undefined): string {
  if (!v) return "";
  return isPercent(k) ? trim(Exact.decimal(v).mul(Exact.int(100)).toFixed(4)) : v;
}
function stored(k: KpiKey, text: string, what: string): string {
  const v = text.trim().replace(/[%,]/g, "");
  if (!DECIMAL.test(v)) throw new Error(`${KPI_DEFINITIONS[k].label}: the ${what} must be a number`);
  return isPercent(k) ? trim(Exact.decimal(v).div(Exact.int(100)).toFixed(6)) : v;
}

type RowDraft = { comparator: "at_least" | "at_most"; target: string; value: string; source: string; year: string };

function draftFor(d: CfoDefaults, industry: Industry): Record<KpiKey, RowDraft> {
  return Object.fromEntries(
    BENCH_KPIS.map((k) => {
      const t = d.targets[industry]?.[k];
      const r = d.references[industry]?.[k];
      const higher = KPI_DEFINITIONS[k].higherIsBetter;
      return [k, { comparator: t?.comparator ?? (higher === false ? "at_most" : "at_least"), target: shown(k, t?.target), value: shown(k, r?.value), source: r?.source ?? "", year: r ? String(r.year) : "" }];
    }),
  ) as Record<KpiKey, RowDraft>;
}

export function CfoDefaultsSettings({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { data: raw, isLoading } = useQuery<Record<string, unknown>>({ queryKey: ["/api/firm/settings"] });
  const saved = cfoDefaultsOf(raw?.cfoDefaults);
  const [picked, setIndustry] = useState<Industry | null>(null);
  // Opens on the first industry with defaults set, if any.
  const industry: Industry = picked ?? ((Object.keys(INDUSTRIES) as Industry[]).find((i) => Object.keys(saved.targets[i] ?? {}).length + Object.keys(saved.references[i] ?? {}).length > 0) ?? "professional");
  const [rows, setRows] = useState<Record<KpiKey, RowDraft> | null>(null);
  useEffect(() => {
    if (raw) setRows(draftFor(cfoDefaultsOf(raw.cfoDefaults), industry));
  }, [raw, industry]);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      const targets: CfoDefaults["targets"][Industry] = {};
      const references: CfoDefaults["references"][Industry] = {};
      for (const k of BENCH_KPIS) {
        const r = rows![k];
        if (r.target.trim()) targets[k] = { comparator: r.comparator, target: stored(k, r.target, "target"), tolerance: saved.targets[industry]?.[k]?.tolerance ?? "0.10" };
        const any = r.value.trim() || r.source.trim() || r.year.trim();
        if (any) {
          if (!r.value.trim() || !r.source.trim() || !/^\d{4}$/.test(r.year.trim())) throw new Error(`${KPI_DEFINITIONS[k].label}: a published figure needs its value, source and year`);
          references[k] = { value: stored(k, r.value, "published figure"), source: r.source.trim(), year: Number(r.year.trim()) };
        }
      }
      const next: CfoDefaults = { targets: { ...saved.targets, [industry]: targets }, references: { ...saved.references, [industry]: references } };
      await api.send("PATCH", "/api/firm/settings", { cfoDefaults: next });
      await queryClient.refetchQueries({ queryKey: ["/api/firm/settings"] });
    },
  });
  if (isLoading || !rows) return <Loading />;
  const set = (k: KpiKey, patch: Partial<RowDraft>) => setRows({ ...rows, [k]: { ...rows[k], ...patch } });
  const counts = (i: Industry) => Object.keys(saved.targets[i] ?? {}).length + Object.keys(saved.references[i] ?? {}).length;
  return (
    <Section
      title="CFO benchmarks"
      description="Per industry: default targets you can copy to a client (from the client's CFO settings), and published figures shown beside your own peers on the Benchmarks tab. Enter percentages as percent, for example 38.5."
      action={
        <select aria-label="Industry" className={select} value={industry} onChange={(e) => setIndustry(e.target.value as Industry)}>
          {(Object.keys(INDUSTRIES) as Industry[]).map((i) => (
            <option key={i} value={i}>
              {INDUSTRIES[i]}
              {counts(i) ? ` (${counts(i)} set)` : ""}
            </option>
          ))}
        </select>
      }
    >
      <TableScroll label="Default KPI settings table" className="rounded-md border border-border">
        <table className="w-full min-w-[52rem] text-sm">
          <thead className="bg-secondary/50 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">KPI</th>
              <th className="px-3 py-2 text-left font-medium">Default target</th>
              <th className="px-3 py-2 text-left font-medium">Published figure</th>
              <th className="px-3 py-2 text-left font-medium">Source</th>
              <th className="px-3 py-2 text-left font-medium">Year</th>
            </tr>
          </thead>
          <tbody>
            {BENCH_KPIS.map((k) => {
              const r = rows[k];
              const unit = isPercent(k) ? "%" : KPI_DEFINITIONS[k].kind === "days" ? "days" : "";
              return (
                <tr key={k} className="border-t border-border align-middle">
                  <td className="px-3 py-1.5 font-medium">{KPI_DEFINITIONS[k].label}</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <select aria-label={`${KPI_DEFINITIONS[k].label} comparison`} disabled={!canEdit} className={cn(select, "h-8")} value={r.comparator} onChange={(e) => set(k, { comparator: e.target.value as RowDraft["comparator"] })}>
                        <option value="at_least">at least</option>
                        <option value="at_most">at most</option>
                      </select>
                      <Input aria-label={`${KPI_DEFINITIONS[k].label} target`} disabled={!canEdit} inputMode="decimal" className="h-8 w-20 text-right tabular" value={r.target} onChange={(e) => set(k, { target: e.target.value })} />
                      <span className="w-8 text-xs text-muted-foreground">{unit}</span>
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <Input aria-label={`${KPI_DEFINITIONS[k].label} published figure`} disabled={!canEdit} inputMode="decimal" className="h-8 w-20 text-right tabular" value={r.value} onChange={(e) => set(k, { value: e.target.value })} />
                      <span className="w-8 text-xs text-muted-foreground">{unit}</span>
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <Input aria-label={`${KPI_DEFINITIONS[k].label} source`} disabled={!canEdit} maxLength={200} placeholder="Publisher and report" className="h-8 w-56" value={r.source} onChange={(e) => set(k, { source: e.target.value })} />
                  </td>
                  <td className="px-3 py-1.5">
                    <Input aria-label={`${KPI_DEFINITIONS[k].label} year`} disabled={!canEdit} inputMode="numeric" maxLength={4} placeholder="2025" className="h-8 w-20 tabular" value={r.year} onChange={(e) => set(k, { year: e.target.value })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
      <p className="mt-2 text-xs text-muted-foreground">Leave a box empty for no default. Published figures should come from a source your firm may use; they are shown with the source and year, and are not mixed into your peers' figures.</p>
      {save.error && <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p>}
      {canEdit && (
        <div className="mt-3 flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            <Save className="h-3.5 w-3.5" /> Save {INDUSTRIES[industry]}
          </Button>
        </div>
      )}
    </Section>
  );
}
